import { IconCircleCheckFilled, IconTag } from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { BRAND_COLORS } from 'brand-guidelines/colors';

import { LoadingSpinner } from 'brand-guidelines/Loading-styled-components';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';

import { FormError } from 'pages/checkout/components/common/input-styled-components';

import { useOfferService } from 'services/OfferService';

import { InputWrapper, SubmitButton, SuccessInput, TransparentFormInput, Wrapper } from './PromoCodeBox-styled-components';

const iconStyles = {
  color: BRAND_COLORS.lightGreen,
  size: 18,
  style: { flexShrink: 0 },
};

const placeholder = 'Add promo code';

export const PromoCodeBox = () => {
  const dispatch = useDispatch();

  const requestOfferLoading = useSelector(RequestOffer.loading!.selector);
  const requestOfferError = useSelector(RequestOffer.error!.selector);

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const autoSubmitTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (autoSubmitTimeout.current) {
      clearTimeout(autoSubmitTimeout.current);
    }

    if (!newValue) {
      return;
    }

    // Auto submit after 2 seconds of inactivity
    autoSubmitTimeout.current = setTimeout(() => {
      onSubmit(newValue);
    }, 2000);
  };

  useEffect(() => {
    // Focus input after error
    if (!requestOfferError) {
      return;
    }
    // Allow disabled to be removed before focusing
    setTimeout(() => {
      inputRef.current?.focus();
    });
  }, [requestOfferError]);

  const onSubmit = useCallback((offerCode: string) => {
    dispatch(RequestOffer.createAction(offerCode));
  }, [dispatch]);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(inputValue);
  }, [onSubmit, inputValue]);

  const { hasActiveOffer, offerName, formattedReferralOfferName } = useOfferService();

  const offerNameToDisplay = useMemo(
    () => formattedReferralOfferName || offerName,
    [formattedReferralOfferName, offerName],
  );

  if (!hasActiveOffer) {
    return null;
  }

  return (
    <>
      <Wrapper
        $isSuccess={hasActiveOffer}
        onClick={() => inputRef.current?.focus()}
        onSubmit={handleSubmit}
      >
        <InputWrapper>
          {hasActiveOffer
            ? <IconCircleCheckFilled {...iconStyles} />
            : <IconTag {...iconStyles} />}

          {hasActiveOffer
            ? <SuccessInput>{offerNameToDisplay}</SuccessInput>
            : (
              <TransparentFormInput
                type="text"
                ref={inputRef}
                value={inputValue}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={requestOfferLoading}
                onBlur={() => onSubmit(inputValue)}
              />
            )}
        </InputWrapper>

        <SubmitButton $loading={requestOfferLoading}>
          {requestOfferLoading && <LoadingSpinner {...iconStyles} />}
        </SubmitButton>
      </Wrapper>
      {requestOfferError && <FormError>{requestOfferError}</FormError>}
    </>
  );
};
